// RoleList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import T from '../../../components/common/T';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axiosConfig';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../../context/AuthContext';
import RoleGate from '../../../components/RoleGate';
import DataTable from '../../../components/common/DataTables.jsx';
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from '../../../components/common/PageLayout';
import { TopPrimaryButton } from '../../../components/common/StandardButtons';
import useModuleFilters from '../../../hooks/useModuleFilters';
import {
  TrashIcon, PencilSquareIcon,
  ShieldCheckIcon, CheckCircleIcon, XCircleIcon
} from '@heroicons/react/24/outline';

const RoleList = () => {
  const navigate = useNavigate();
  const { checkAccess } = useAuth();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("roles", {
    statuses: ['Active', 'Inactive'],
  });

  const fetchRoles = async () => {
    try {
      const res = await api.get("/roles");
      const safeRoles = (res.data || []).filter(role =>
        role.name.toLowerCase() !== 'superadmin' &&
        role.name.toLowerCase() !== 'super admin'
      );
      setRoles(safeRoles);
    } catch (err) {
      console.warn("Failed to load roles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoles(); }, []);

  const handleDeleteClick = (id) => { setSelectedRoleId(id); setIsModalOpen(true); };

  const confirmDelete = async () => {
    try {
      await api.delete(`/roles/${selectedRoleId}`);
      toast.success("Role deleted successfully!");
      setRoles(prev => prev.filter(r => r.id !== selectedRoleId));
      setIsModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete role");
    }
  };

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => [
    {
      label: "Total Roles",
      value: roles.length,
      icon: ShieldCheckIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => setStatusFilter("All"),
    },
    {
      label: "Active",
      value: roles.filter(r => (r.status || 'Active') === 'Active').length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "Active",
      onClick: () => setStatusFilter(s => s === "Active" ? "All" : "Active"),
    },
    {
      label: "Inactive",
      value: roles.filter(r => r.status === 'Inactive').length,
      icon: XCircleIcon,
      variant: "gray",
      isActive: statusFilter === "Inactive",
      onClick: () => setStatusFilter(s => s === "Inactive" ? "All" : "Inactive"),
    },
  ], [roles, statusFilter]);

  const filteredRoles = useMemo(() => {
    let result = [...roles];
    if (statusFilter && statusFilter !== "All") {
      result = result.filter(r => (r.status || 'Active').toLowerCase() === statusFilter.toLowerCase());
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(r =>
        (r.name || '').toLowerCase().includes(s) ||
        (r.description || '').toLowerCase().includes(s)
      );
    }
    return result;
  }, [roles, statusFilter, searchTerm]);

  const columns = [
    {
      header: "Role Name", accessor: "name", sortable: true,
      render: (role) => <span className="font-bold text-gray-900 dark:text-white">{role.name}</span>
    },
    {
      header: "Description", accessor: "description", sortable: true,
      render: (role) => <span className="text-gray-500 dark:text-slate-400">{role.description || "-"}</span>
    },
    {
      header: "Permissions", align: "center",
      render: () => <span className="bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 px-2 py-1 rounded text-xs font-medium border border-gray-200 dark:border-slate-700"><T>Configured</T></span>
    },
    {
      header: "Status", accessor: "status", align: "center", sortable: true,
      render: (role) => (
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${(role.status || 'Active') === 'Active' ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/60' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-700'}`}>
          {role.status || 'Active'}
        </span>
      )
    },
    {
      header: "Actions", align: "right",
      render: (role) => (
        <div className="flex justify-end gap-2">
          {checkAccess('role.edit') && (
            <button onClick={() => navigate(`/admin/roles/edit/${role.id}`)}
              className="p-2 bg-white dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:border-amber-200 transition shadow-2xs cursor-pointer" title="Edit Role">
              <PencilSquareIcon className="h-4 w-4" />
            </button>
          )}
          {checkAccess('role.delete') && (
            <button onClick={() => handleDeleteClick(role.id)}
              className="p-2 bg-white dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] text-red-600 dark:text-rose-400 rounded-lg hover:bg-red-50 dark:hover:bg-rose-950/40 hover:border-red-200 transition shadow-2xs cursor-pointer" title="Delete Role">
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      )
    }
  ];

  if (loading) return <div className="p-10 text-center text-gray-500 dark:text-slate-400"><T>Loading Roles...</T></div>;

  return (
    <PageLayout>
      <ConfirmationModal
        isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={confirmDelete}
        title="Delete Role?" message="Are you sure you want to delete this role? This action cannot be undone."
        confirmText="Yes, Delete" isDanger={true}
      />

      {/* Header */}
      <PageHeader
        icon={ShieldCheckIcon}
        iconColor="text-[#E8612C]"
        title="Role Management"
        subtitle="Manage user access levels and permissions"
        onRefresh={fetchRoles}
      >
        {checkAccess('role.create') && (
          <TopPrimaryButton
            onClick={() => navigate('/admin/roles/add')}
            label="Create New Role"
          />
        )}
      </PageHeader>

      {/* Stats */}
      <StatsGrid stats={stats} cols={3} />

      {/* Unified Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search role name or description..."
        tabs={['All', 'Active', 'Inactive']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      />

      {/* Collapsible Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => { setStatusFilter("All"); setSearchTerm(""); }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Role Status</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-sm bg-white dark:bg-[#21262d] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Statuses</T></option>
            {(filterOptions.statuses || ['Active', 'Inactive']).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {/* Table */}
      <TableCard>
        <DataTable data={filteredRoles} columns={columns} emptyMessage="No roles found." />
      </TableCard>
    </PageLayout>
  );
};

export default RoleList;